import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Heart } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const footerLinks = [
    {
      title: t('footer.platform'),
      links: [
        { label: t('common.home'), path: '/' },
        { label: t('common.search'), path: '/search' }
      ]
    },
    {
      title: t('footer.services'),
      links: [
        { label: t('common.addProperty'), path: '/add-property' },
        { label: t('common.buyTokens'), path: '/buy-tokens' },
        { label: t('profile.favorites'), path: '/favorites' },
        { label: t('common.myProperties'), path: '/my-properties' },
        { label: t('profile.purchasedContacts'), path: '/purchased-contacts' }
      ]
    }
  ];

  const socialLinks = [
    { icon: <Facebook size={20} />, href: '#', label: 'Facebook' },
    { icon: <Twitter size={20} />, href: '#', label: 'Twitter' },
    { icon: <Instagram size={20} />, href: '#', label: 'Instagram' },
    { icon: <Linkedin size={20} />, href: '#', label: 'LinkedIn' }
  ];

  return (
    <footer className="bg-neutral-900 text-neutral-300">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <img
                src="/stanorama-logo-negative.svg"
                alt="Stanorama"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-neutral-400 mb-6 leading-relaxed">
              {t('footer.tagline')}
            </p>
            
            {/* Contact info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-primary-400" />
                <span className="text-sm">stanorama.rs@gmail.com</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-primary-400" />
                <span className="text-sm">+381 (100) 123-4567</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-primary-400" />
                <span className="text-sm">Novi Sad, Srbija</span>
              </div>
            </div>

            {/* Social links */}
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 bg-neutral-800 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Footer links */}
          {footerLinks.map((section, index) => (
            <div key={index} className="lg:col-span-1">
              <h3 className="text-white font-semibold mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-neutral-400 hover:text-white transition-colors duration-200 text-sm block"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-neutral-400">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>
            <div className="flex items-center gap-1 text-sm text-neutral-400">
              {t('footer.madeWith')} <Heart size={14} className="text-red-500 mx-1" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
